<script setup>
import FrontendBreadcrumbs from "@/components/navigation/FrontendBreadcrumbs";
import ActionDropdown from "@/components/navigation/ActionDropdown";
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";
import {computed} from "vue";

const props = defineProps({
  imageFileName: {
    type: String,
    default: null,
  },
  hideBreadcrumbs: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: null,
  },
  titleIcon: {
    type: String,
    default: null,
  },
  // Tone of the fallback crest tile + icon. Entity identity tones:
  // leagues/default 'primary', meets 'success', ladders 'ladder' (blue),
  // tournaments 'warning', americanos 'americano' (teal) — matching the
  // dashboard format cards.
  titleIconColor: {
    type: String,
    default: 'primary',
  },
  subTitle: {
    type: String,
    default: null,
  },
  actions: {
    type: Array,
    default: () => [],
  },
  user: {
    type: Object,
    default: null,
  },
  howTo: {
    type: Object,
    default: null,
  },
  club: {
    type: Object,
    default: null,
  },
  league: {
    type: Object,
    default: null,
  },
  ladder: {
    type: Object,
    default: null,
  },
  ladderChallenge: {
    type: Object,
    default: null,
  },
  tournament: {
    type: Object,
    default: null,
  },
  meet: {
    type: Object,
    default: null,
  },
  meetOccurrence: {
    type: Object,
    default: null,
  },
  americano: {
    type: Object,
    default: null,
  },
  americanoCycle: {
    type: Object,
    default: null,
  },
  cycle: {
    type: Object,
    default: null,
  },
  scoringSystem: {
    type: Object,
    default: null,
  },
  // Names the current page in the trail when the route can't (e.g. a membership type's name). X56
  finalBreadcrumb: {
    type: String,
    default: null,
  },
  // An explicit trail after Home ([{title, to?}]); the last is the current page. X56
  breadcrumbItems: {
    type: Array,
    default: null,
  },
});

/**
 * Redesign "one red" rule: header CTAs are brand-red, not blue/green.
 * Legacy pages tag actions `success` (green) / `info` (blue); re-map to the
 * brand palette — filled brand for primary actions, outlined brand for
 * secondary (info) — keeping error/warning meaning.
 */
/**
 * Initials fallback for the crest when no image/icon is supplied — first letter
 * of up to the first two words of the title (e.g. "Grant Carlisle" → "GC").
 */
const crestInitials = computed(() => {
  if (!props.title) return '';
  return props.title
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
});

const actionColor = (action) => {
  if (action.color === 'error' || action.color === 'warning') return action.color;
  return 'primary';
};
const actionVariant = (action) => {
  if (action.color === 'info' || action.color === 'error' || action.color === 'warning') return 'outlined';
  return 'flat';
};
</script>

<template>
  <div>
    <frontend-breadcrumbs
        v-if="!hideBreadcrumbs"
        class="mb-3 fh-breadcrumbs"
        :user="user"
        :club="club"
        :how-to="howTo"
        :league="league"
        :ladder-challenge="ladderChallenge"
        :ladder="ladder"
        :tournament="tournament"
        :meet="meet"
        :meet-occurrence="meetOccurrence"
        :americano="americano"
        :americano-cycle="americanoCycle"
        :items="breadcrumbItems"
        :final-breadcrumb="finalBreadcrumb"
    />
    <div class="d-flex align-center">
      <!-- Rounded crest: image, else icon tile, else initials tile (matches the
           sidebar .tl-crest look — rounded corners, never a circle) -->
      <avatar-image-loader
          v-if="imageFileName"
          :image-file-name="imageFileName"
          rounded="lg"
          size="64"
          class="mr-3 bg-white fh-crest"/>
      <div
          v-else-if="titleIcon || crestInitials"
          class="mr-3 fh-crest fh-crest--fallback"
          :class="titleIconColor !== 'primary' ? `fh-crest--${titleIconColor}` : ''">
        <!-- no color prop: the icon inherits the tile's tone colour -->
        <v-icon v-if="titleIcon" size="30">{{ titleIcon }}</v-icon>
        <span v-else class="fh-crest-initials">{{ crestInitials }}</span>
      </div>
      <!-- min-width 0 so a one-line subtitle (CompetitionSubtitle) can truncate with an ellipsis -->
      <div class="flex-fill fh-text">
        <slot name="title">
          <h1 v-if="title" class="mb-0 flex-fill">{{ title }}</h1>
        </slot>
        <slot name="subTitle">
          <p v-if="subTitle" class="mb-0 flex-fill fh-subtitle">{{ subTitle }}</p>
        </slot>
      </div>
      <div class="d-flex justify-end flex-wrap" v-if="actions">
        <v-btn
            v-for="action in actions"
            :key="action.title"
            :loading="action.loading"
            class="ml-1 d-none d-md-inline-flex mb-1"
            size="small"
            :to="action.route"
            :color="actionColor(action)"
            :variant="actionVariant(action)"
            @click="() => action.click ? action.click() : null"
        >
          <v-icon v-if="action.icon" size="13" class="mr-1">{{ action.icon }}</v-icon>
          {{ action.title }}
        </v-btn>
        <v-list-item-action class="d-block d-md-none">
          <action-dropdown v-if="actions.length > 0" icon="mdi-cog" :items="actions" />
        </v-list-item-action>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Entity crest/logo — match the app's card corners (14px), not a sharp tile */
.fh-crest,
:deep(.fh-crest) {
  border-radius: 14px !important;
}

/* Fallback crest when no image: icon or initials in a brandTint tile */
.fh-crest--fallback {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.fh-crest--success {
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
}

/* ladder blue — same pair as the dashboard format card (.cfc-ic--ladder) */
.fh-crest--ladder {
  background: #E8F1FB;
  color: #2477C8;
}

.fh-crest--warning {
  background: rgb(var(--v-theme-warningTint));
  color: rgb(var(--v-theme-warning));
}

/* americano purple — same pair everywhere (#F0EAFB tint / #7C3AED ink) */
.fh-crest--americano {
  background: #F0EAFB;
  color: #7C3AED;
}

.fh-crest-initials {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1;
  color: rgb(var(--v-theme-primary));
}

/* Page title — 22px mobile / 24px desktop, 800, tight tracking */
h1,
:slotted(h1) {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.25;
}

@media (min-width: 600px) {
  h1,
  :slotted(h1) {
    font-size: 24px;
  }
}

/* Title column — may shrink below its content so long one-line text truncates */
.fh-text {
  min-width: 0;
}

/* Subtitle — 12.5px subtle */
.fh-subtitle {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.6;
}

/* Breadcrumbs — flatten old white card; 11.5px / 600 / subtle */
.fh-breadcrumbs {
  background: transparent !important;
  padding: 0 !important;
}

.fh-breadcrumbs :deep(.v-breadcrumbs) {
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

.fh-breadcrumbs :deep(a) {
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle)) !important;
  background: transparent !important;
  padding: 0 !important;
}

.fh-breadcrumbs :deep(a:hover) {
  color: rgb(var(--v-theme-charcoal)) !important;
}

/* current page crumb reads slightly darker */
.fh-breadcrumbs :deep(a[aria-current="page"]) {
  color: rgb(var(--v-theme-charcoal)) !important;
}

/* separator arrows */
.fh-breadcrumbs :deep(a .v-icon) {
  font-size: 14px;
  color: rgb(var(--v-theme-border));
  margin: 0 2px;
}

/* hide the default "/" divider — the arrow icon is the separator */
.fh-breadcrumbs :deep(.v-breadcrumbs-divider) {
  display: none;
}
</style>
